import { ApiError } from 'services/api';
import { getXsrfToken } from 'utils/cookies';
import { HUMAN_READABLE_ITERABLE_EVENTS } from 'constants/eventTypes';
import { EventTypes } from 'modules/domains/segmentation';
import { CompleteCondition } from 'modules/queryBuilder/types';
import { MeterOptions } from 'ui/chart';
import { BaseOption } from 'ui/selects';
import { standardChartColors } from 'ui/theme';
import { isObject } from 'utils/helpers';
import { AudienceCount, BehavioralEvent, PostFormParams, PostFormParamsKey, User } from './types';

// Returns label information for behavioral event given data type
export const getBehavioralEventLabel = (
  dataType: EventTypes,
  events: Record<EventTypes, string>,
  defaultName: string,
) => (events[dataType] !== events[EventTypes.CustomEvent] ? events[dataType] : defaultName);

// Returns options for behavioral event select
export const getBehavioralEventSelectOptions = (options: BehavioralEvent[]) =>
  options.map<BaseOption<string>>(({ event, dataType }) => ({
    id: `behavior-event-${event}-${dataType}`,
    label: getBehavioralEventLabel(dataType, HUMAN_READABLE_ITERABLE_EVENTS, event),
    value: event,
    Suffix: dataType === EventTypes.CustomEvent ? 'custom' : 'system',
  }));

export const retryOnPartialContent = (failureCount: number, error: ApiError) =>
  error.response?.status === 206;

export const getMeterOptions = (values: AudienceCount[]) => {
  const colorPalette = Object.values(standardChartColors);

  return values.map<MeterOptions>(({ name, count }, i) => ({
    name,
    value: count,
    tooltipContent: name,
    color: colorPalette[i],
  }));
};

/** copies logic used in Angular segmentation to export from an endpoint */
export const postFormToEndpoint = (endpoint: string, params: PostFormParams) => {
  const csrfToken = getXsrfToken();
  const form = document.createElement('form');
  form.action = endpoint;
  form.method = 'POST';
  form.target = '_blank';
  const dataWithToken = { ...params, csrfToken };

  Object.keys(dataWithToken).forEach((el: string) => {
    const input = document.createElement('textarea');
    input.name = el;
    const value = dataWithToken[el as PostFormParamsKey];
    if (value) {
      input.value = typeof value === 'object' ? JSON.stringify(value) : value;
      form.appendChild(input);
    }
  });

  form.style.display = 'none';
  document.body.appendChild(form);
  form.submit();
};

// handles getting the field value of a row, more complex since the field can be referencing a nested object
export const getColumnCellValue = (row: User, field: string) => {
  const splitKeys = field.split('.');
  const storedValues: (Record<string, any> | any)[] = [row];
  splitKeys.forEach((key, i) => {
    const newValue = isObject(storedValues[i]) ? storedValues[i][key] : '';
    storedValues.push(newValue);
  });
  return storedValues[storedValues.length - 1];
};

export const hasCatalogQuery = (condition: CompleteCondition): boolean =>
  'catalogQuery' in condition && 'catalogField' in condition;
